import React from 'react';
import { Box, Typography, Tab, Tabs, Chip } from '@mui/material';
import TankIcon from '@mui/icons-material/Water';

/**
 * 製品選択用のタブコンポーネント
 * 論理削除された製品を適切に処理
 */
const ProductTabs = ({ 
  products,
  clientProducts,
  tabValue,
  onTabChange,
  onProductChange
}) => {
  // 🚀 安全性チェック：無効なデータを除外
  const safeProducts = React.useMemo(() => {
    if (!Array.isArray(products)) return [];
    
    return products.filter(product => 
      product && 
      product.id && 
      product.name && 
      product.active !== false // 論理削除チェック
    );
  }, [products]);

  const safeClientProducts = React.useMemo(() => {
    if (!Array.isArray(clientProducts)) return [];
    
    // 有効な製品IDに関連付けられた顧客製品のみ
    const validProductIds = new Set(safeProducts.map(p => p.id));
    
    return clientProducts.filter(cp => 
      cp && 
      cp.id && 
      cp.productId && 
      validProductIds.has(cp.productId)
    );
  }, [clientProducts, safeProducts]);

  // 🚀 タブ値の安全性チェック（条件文の外で常に実行）
  const safeTabValue = React.useMemo(() => {
    if (tabValue >= 0 && tabValue < safeProducts.length) {
      return tabValue;
    }
    return 0; // デフォルト値
  }, [tabValue, safeProducts.length]);

  // 製品が存在しない場合は何も表示しない
  if (!safeProducts || safeProducts.length === 0) {
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          利用可能な製品がありません。製品を登録してください。
        </Typography>
      </Box>
    );
  }
  
  // 製品が1つしかない場合はタブの代わりにタイトルを表示
  if (safeProducts.length === 1) {
    const product = safeProducts[0];
    const productName = product.name || '未定義製品';
    const clientProduct = safeClientProducts.find(cp => cp.productId === product.id);
    
    return (
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TankIcon color="primary" />
        <Typography variant="h6" fontWeight="medium">
          {productName}
          {!clientProduct && (
            <Chip 
              label="未設定" 
              size="small" 
              variant="outlined"
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          )}
        </Typography>
      </Box>
    );
  }
  
  // 複数製品がある場合はタブで表示
  return (
    <Tabs 
      value={safeTabValue}
      onChange={(event, newValue) => {
        // 🚀 範囲チェック
        if (newValue >= 0 && newValue < safeProducts.length) {
          onTabChange && onTabChange(event, newValue);
        } else {
        }
      }}
      variant="scrollable"
      scrollButtons="auto"
      sx={{ 
        mb: 2,
        borderBottom: 1,
        borderColor: 'divider',
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 500
        }
      }}
    >
      {safeProducts.map((product, index) => {
        // この製品が顧客に関連付けられているか確認
        const clientProduct = safeClientProducts.find(cp => cp.productId === product.id);
        const hasData = clientProduct !== undefined;
        
        return (
          <Tab 
            key={`product-tab-${product.id}`} // 🚀 一意性を保証
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {product.name}
                {!hasData && (
                  <Chip 
                    label="未設定" 
                    size="small" 
                    variant="outlined"
                    sx={{ ml: 0.5, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            }
            //icon={<TankIcon />}
            iconPosition="start"
            // 🚀 アクセシビリティ向上
            aria-label={`製品: ${product.name}${!hasData ? ' (未設定)' : ''}`}
          />
        );
      })}
    </Tabs>
  );
};

export default ProductTabs;